import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "@api/axios";
import useAuthenticatedQuery from "@hooks/useAuthenticatedQuery";
import { useSession } from "@hooks/useSession";

const useNotifications = () => {
  const queryClient = useQueryClient();
  const { token } = useSession();
  const language = localStorage.getItem("ln") || "en"; // <-- Language fallback

  // Fetch all notifications
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    error: notificationsError,
    refetch: refetchNotifications,
  } = useAuthenticatedQuery(["notifications"], "/api/v1/notifications", {
    queryFn: async () => {
      const response = await axios.get("/api/v1/notifications", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Accept-Language": language, // <-- Inject Accept-Language
        },
        // No limit parameter - API uses its own default page size
      });
      return response.data;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Fetch unread notifications
  const {
    data: unreadData,
    isLoading: unreadLoading,
    error: unreadError,
    refetch: refetchUnread,
  } = useAuthenticatedQuery(["unreadNotifications"], "/api/v1/notifications/unread", {
    queryFn: async () => {
      const response = await axios.get("/api/v1/notifications/unread", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Accept-Language": language, // <-- Inject Accept-Language
        },
      });
      return response.data;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: true, // Refetch when window gains focus
  });

  // Mutation for bookmarking a notification
  const bookmarkMutation = useMutation({
    mutationFn: (id) =>
      axios.post(`/api/v1/notifications/bookmark/${id}`, null, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "Accept-Language": language, // <-- Inject Accept-Language
        },
      }),
    onSuccess: async () => {
      // Invalidate and wait for refetch to complete
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notifications"], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ["unreadNotifications"], refetchType: 'active' }),
      ]);
    },
    onError: (error) => {
      console.error("Error bookmarking notification:", error);
    },
  });

  return {
    notifications: notificationsData?.records || [],
    unreadNotifications: unreadData?.records || [],
    unreadCount: unreadData?.total_count || 0,
    notificationsLoading,
    unreadLoading,
    notificationsError,
    unreadError,
    bookmarkNotification: bookmarkMutation.mutateAsync,
    refetchNotifications,
    refetchUnread,
  };
};

export default useNotifications;
